import { HsVector2 } from '../../math-new/objects/vector2.class';
import type { HsVector3 } from '../../math-new/objects/vector3.class';
import { HsControlActionType } from '../actions/control-action-type.enum';
import { HsMouseMoveControlAction } from '../actions/mouse-move-control-action.class';
import { HsZoomControlAction } from '../actions/zoom-control-action.class';
import type { HsControlActionsHandler } from '../camera/control-actions-handler.class';
import { HsToolsConstants } from '../tools-constants.constants';
import {
  HS_DEFAULT_TOUCH_NAVIGATION_SETTINGS,
  type HsTouchNavigationSettings,
} from './touch-navigation-settings.interface';

/** The subset of a DOM `PointerEvent` the recognizer reads, so it can be driven without a DOM. */
export interface HsTouchPointerInput {
  pointerId: number;
  pointerType: string;
  clientX: number;
  clientY: number;
  timeStamp: number;
}

export interface HsTouchGestureCallbacks {
  /**
   * Returns the world point under a screen position, or `null` while it is unknown. It is called when a
   * pinch starts and on each pinch move until it returns a point, so it may start an asynchronous pick and
   * answer from it later. `gestureId` changes with every new pinch.
   */
  resolvePivot?: (position: HsVector2, gestureId: number) => HsVector3 | null;
  /** Zoom step distance while no pivot is known, for example the distance to the scene bounds center. */
  getFallbackDistance?: () => number;
  /** Multiplier for two-finger pan, for example from the distance to the content under the fingers. */
  getPanScale?: () => number;
  onDoubleTap?: (position: HsVector2) => void;
  /** Called after control actions were queued, so the host can schedule a frame. */
  onNavigate?: () => void;
}

interface TapRecord {
  position: HsVector2;
  time: number;
}

/**
 * Translates touch pointer events into camera control actions:
 * one finger orbits, two fingers pinch-zoom toward the point under them and pan, a double tap reports
 * the tapped position. Non-touch pointers are ignored, so mouse and pen input keep their own handlers.
 */
export class HsTouchGestureRecognizer {
  public settings: HsTouchNavigationSettings;

  private readonly pointers: Map<number, HsVector2> = new Map<number, HsVector2>();
  private pinchDistance: number | null = null;
  private readonly pinchCenter: HsVector2 = new HsVector2(0, 0);
  private pinchPivot: HsVector3 | null = null;
  private gestureId: number = 0;
  private multiTouch: boolean = false;
  private dragging: boolean = false;
  private touchStart: TapRecord | null = null;
  private lastTap: TapRecord | null = null;

  constructor(
    private readonly controlActionsHandler: HsControlActionsHandler,
    private readonly callbacks: HsTouchGestureCallbacks = {},
    settings: Partial<HsTouchNavigationSettings> = {}
  ) {
    this.settings = { ...HS_DEFAULT_TOUCH_NAVIGATION_SETTINGS, ...settings };
  }

  public get activePointerCount(): number {
    return this.pointers.size;
  }

  /** Returns `true` when the event belongs to a touch gesture and the host should suppress its default action. */
  public pointerDown(input: HsTouchPointerInput): boolean {
    if (input.pointerType !== 'touch') {
      return false;
    }

    this.pointers.set(input.pointerId, new HsVector2(input.clientX, input.clientY));
    if (this.pointers.size === 1) {
      this.multiTouch = false;
      this.dragging = false;
      this.touchStart = { position: new HsVector2(input.clientX, input.clientY), time: input.timeStamp };
    } else {
      this.multiTouch = true;
      this.touchStart = null;
    }
    this.restartPinch();
    return true;
  }

  public pointerMove(input: HsTouchPointerInput): boolean {
    if (input.pointerType !== 'touch') {
      return false;
    }

    const previous: HsVector2 | undefined = this.pointers.get(input.pointerId);
    if (previous === undefined) {
      return false;
    }

    const current: HsVector2 = new HsVector2(input.clientX, input.clientY);
    this.pointers.set(input.pointerId, current);

    if (this.pointers.size === 1) {
      this.moveSinglePointer(previous, current);
    } else {
      this.movePinch();
    }
    return true;
  }

  public pointerUp(input: HsTouchPointerInput): boolean {
    if (!this.pointers.has(input.pointerId)) {
      return input.pointerType === 'touch';
    }

    this.pointers.delete(input.pointerId);
    if (this.pointers.size === 0) {
      this.detectTap(input);
      this.touchStart = null;
    }
    this.restartPinch();
    return true;
  }

  public pointerCancel(input: HsTouchPointerInput): boolean {
    if (!this.pointers.has(input.pointerId)) {
      return input.pointerType === 'touch';
    }

    this.pointers.delete(input.pointerId);
    this.touchStart = null;
    this.lastTap = null;
    this.restartPinch();
    return true;
  }

  public reset(): void {
    this.pointers.clear();
    this.touchStart = null;
    this.lastTap = null;
    this.multiTouch = false;
    this.dragging = false;
    this.restartPinch();
  }

  private moveSinglePointer(previous: HsVector2, current: HsVector2): void {
    // Lifting one finger of a pinch must not turn the remaining finger into an unintended orbit.
    if (this.multiTouch) {
      return;
    }

    let from: HsVector2 = previous;
    if (!this.dragging) {
      const start: HsVector2 | undefined = this.touchStart?.position;
      if (start !== undefined && this.distance(start, current) <= this.settings.tapMaxMovementPx) {
        return;
      }
      // Apply the movement swallowed by the tap threshold, so the orbit does not lag behind the finger.
      from = start ?? previous;
      this.dragging = true;
    }

    const sensitivity: number = this.settings.rotateSensitivity;
    // The orbit drag of the mouse (right button), so one finger orbits around the pivot as it does.
    this.controlActionsHandler.updateControlAction(
      new HsMouseMoveControlAction(
        HsControlActionType.RightMove,
        new HsVector2((from.x - current.x) * sensitivity, (from.y - current.y) * sensitivity)
      )
    );
    this.callbacks.onNavigate?.();
  }

  private movePinch(): void {
    const [first, second]: HsVector2[] = Array.from(this.pointers.values());
    if (first === undefined || second === undefined) {
      return;
    }

    const distance: number = this.distance(first, second);
    const center: HsVector2 = new HsVector2((first.x + second.x) / 2, (first.y + second.y) / 2);
    const previousCenter: HsVector2 = new HsVector2(this.pinchCenter.x, this.pinchCenter.y);
    const previousDistance: number | null = this.pinchDistance;
    this.pinchCenter.copy(center);
    this.pinchDistance = distance;
    if (previousDistance === null) {
      return;
    }

    let navigated: boolean = false;
    const panX: number = previousCenter.x - center.x;
    const panY: number = previousCenter.y - center.y;
    if (panX !== 0 || panY !== 0) {
      const panScale: number = this.settings.panSensitivity * (this.callbacks.getPanScale?.() ?? 1);
      this.controlActionsHandler.updateControlAction(
        new HsMouseMoveControlAction(HsControlActionType.MiddleMove, new HsVector2(panX * panScale, panY * panScale))
      );
      navigated = true;
    }

    const minDistance: number = this.settings.minPinchDistancePx;
    if (distance >= minDistance && previousDistance >= minDistance && distance !== previousDistance) {
      const maxScale: number = Math.max(1, this.settings.maxPinchScalePerEvent);
      const ratio: number = Math.min(maxScale, Math.max(1 / maxScale, distance / previousDistance));
      if (this.pinchPivot === null) {
        this.pinchPivot = this.callbacks.resolvePivot?.(center, this.gestureId) ?? null;
      }
      this.controlActionsHandler.updateControlAction(
        new HsZoomControlAction(
          center,
          Math.pow(ratio, this.settings.zoomSensitivity),
          this.pinchPivot,
          this.callbacks.getFallbackDistance?.() ?? HsToolsConstants.ZOOM_FALLBACK_DISTANCE
        )
      );
      navigated = true;
    }

    if (navigated) {
      this.callbacks.onNavigate?.();
    }
  }

  private restartPinch(): void {
    const [first, second]: HsVector2[] = Array.from(this.pointers.values());
    this.pinchPivot = null;
    if (first === undefined || second === undefined) {
      this.pinchDistance = null;
      return;
    }

    this.gestureId++;
    this.pinchDistance = this.distance(first, second);
    this.pinchCenter.set((first.x + second.x) / 2, (first.y + second.y) / 2);
    this.pinchPivot =
      this.callbacks.resolvePivot?.(new HsVector2(this.pinchCenter.x, this.pinchCenter.y), this.gestureId) ?? null;
  }

  private detectTap(input: HsTouchPointerInput): void {
    const start: TapRecord | null = this.touchStart;
    if (start === null || this.multiTouch || this.dragging) {
      this.lastTap = null;
      return;
    }

    const position: HsVector2 = new HsVector2(input.clientX, input.clientY);
    if (
      input.timeStamp - start.time > this.settings.tapMaxDurationMs ||
      this.distance(start.position, position) > this.settings.tapMaxMovementPx
    ) {
      this.lastTap = null;
      return;
    }

    const lastTap: TapRecord | null = this.lastTap;
    if (
      lastTap !== null &&
      input.timeStamp - lastTap.time <= this.settings.doubleTapMaxDelayMs &&
      this.distance(lastTap.position, position) <= this.settings.doubleTapMaxDistancePx
    ) {
      this.lastTap = null;
      this.callbacks.onDoubleTap?.(position);
      return;
    }

    this.lastTap = { position, time: input.timeStamp };
  }

  private distance(first: HsVector2, second: HsVector2): number {
    return Math.hypot(first.x - second.x, first.y - second.y);
  }
}
